import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Progress } from './ui/progress';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { profileApi, verificationApi } from '../lib/api';
import {
  CheckCircle, Circle, User, MapPin, Shield, CreditCard,
  Instagram, Twitter, Youtube, Loader2, ExternalLink, Link as LinkIcon,
  AlertCircle
} from 'lucide-react';

const SOCIAL_PLATFORMS = [
  { id: 'instagram', name: 'Instagram', icon: Instagram, placeholder: 'https://instagram.com/username' },
  { id: 'twitter', name: 'Twitter/X', icon: Twitter, placeholder: 'https://twitter.com/username' },
  { id: 'youtube', name: 'YouTube', icon: Youtube, placeholder: 'https://youtube.com/@channel' },
  { id: 'tiktok', name: 'TikTok', icon: LinkIcon, placeholder: 'https://tiktok.com/@username' },
  { id: 'linkedin', name: 'LinkedIn', icon: LinkIcon, placeholder: 'https://linkedin.com/in/username' },
  { id: 'spotify', name: 'Spotify', icon: LinkIcon, placeholder: 'https://open.spotify.com/artist/...' },
];

const ProfileCompletionModal = ({ isOpen, onClose, onComplete, redirectAfter = null }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [completionStatus, setCompletionStatus] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  
  // Form data
  const [formData, setFormData] = useState({
    bio: '',
    location: '',
    linked_socials: []
  });
  
  // Social link being added
  const [newSocial, setNewSocial] = useState({ platform: '', url: '' });

  useEffect(() => {
    if (isOpen) {
      loadCompletionStatus();
    }
  }, [isOpen]);

  const loadCompletionStatus = async () => {
    setLoading(true);
    try {
      const response = await profileApi.getCompletionStatus();
      setCompletionStatus(response.data);
      
      // Determine starting step based on what's missing
      const missing = response.data.requirements.filter(r => !r.completed);
      if (missing.length > 0) {
        const stepOrder = ['bio', 'location', 'social_links', 'id_verification', 'payment_method'];
        const firstMissing = missing.find(m => stepOrder.includes(m.key));
        if (firstMissing) {
          setCurrentStep(stepOrder.indexOf(firstMissing.key));
        }
      }
    } catch (error) {
      console.error('Failed to load completion status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await profileApi.updateCompletion({
        bio: formData.bio || undefined,
        location: formData.location || undefined,
        linked_socials: formData.linked_socials.length > 0 ? formData.linked_socials : undefined
      });
      toast.success('Profile updated!');
      await loadCompletionStatus();
      
      // Move to next step
      setCurrentStep(prev => prev + 1);
    } catch (error) {
      toast.error('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const handleStartVerification = async () => {
    setSaving(true);
    try {
      const returnUrl = `${window.location.origin}/settings?verification=complete`;
      const response = await verificationApi.createSession(returnUrl);
      
      if (response.data.url) {
        // If there's a note about Stripe Identity not being enabled
        if (response.data.note) {
          toast.info(response.data.note);
        }
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error('Failed to start verification');
    } finally {
      setSaving(false);
    }
  };

  const handleAddPaymentMethod = async () => {
    // In production, this would redirect to Stripe to add payment method
    // For now, we'll simulate adding a payment method
    setSaving(true);
    try {
      await profileApi.addPaymentMethod();
      toast.success('Payment method added!');
      await loadCompletionStatus();
      setCurrentStep(prev => prev + 1);
    } catch (error) {
      toast.error('Failed to add payment method');
    } finally {
      setSaving(false);
    }
  };

  const addSocialLink = () => {
    if (!newSocial.platform || !newSocial.url) {
      toast.error('Please select a platform and enter URL');
      return;
    }
    if (formData.linked_socials.length >= 3) {
      toast.error('Maximum 3 social links allowed');
      return;
    }
    
    const platform = SOCIAL_PLATFORMS.find(p => p.id === newSocial.platform);
    setFormData(prev => ({
      ...prev,
      linked_socials: [...prev.linked_socials, {
        platform: newSocial.platform,
        url: newSocial.url,
        username: newSocial.url.split('/').pop()
      }]
    }));
    setNewSocial({ platform: '', url: '' });
  };

  const removeSocialLink = (index) => {
    setFormData(prev => ({
      ...prev,
      linked_socials: prev.linked_socials.filter((_, i) => i !== index)
    }));
  };

  const handleComplete = () => {
    onComplete?.();
    onClose();
    if (redirectAfter) {
      navigate(redirectAfter);
    }
  };

  const steps = [
    {
      key: 'bio',
      title: 'Tell us about yourself',
      description: 'Add a short bio to help others know you better',
      icon: User,
      content: (
        <div className="space-y-4">
          <div>
            <Label htmlFor="bio">Your Bio</Label>
            <Textarea
              id="bio"
              placeholder="Tell us about yourself, your experience, and what makes you unique..."
              value={formData.bio}
              onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
              className="mt-2 min-h-[120px]"
              data-testid="completion-bio"
            />
          </div>
          <Button 
            onClick={handleSaveProfile} 
            disabled={!formData.bio || saving}
            className="w-full"
            data-testid="save-bio-btn"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Continue
          </Button>
        </div>
      )
    },
    {
      key: 'location',
      title: 'Where are you located?',
      description: 'Add your location to help clients find you',
      icon: MapPin,
      content: (
        <div className="space-y-4">
          <div>
            <Label htmlFor="location">Your Location</Label>
            <Input
              id="location"
              placeholder="City, State or Country"
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              className="mt-2"
              data-testid="completion-location"
            />
          </div>
          <Button 
            onClick={handleSaveProfile} 
            disabled={!formData.location || saving}
            className="w-full"
            data-testid="save-location-btn"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Continue
          </Button>
        </div>
      )
    },
    {
      key: 'social_links',
      title: 'Link your social media',
      description: 'Connect up to 3 social profiles to build trust',
      icon: LinkIcon,
      content: (
        <div className="space-y-4">
          {/* Existing linked socials */}
          {formData.linked_socials.length > 0 && (
            <div className="space-y-2">
              {formData.linked_socials.map((social, idx) => {
                const platform = SOCIAL_PLATFORMS.find(p => p.id === social.platform);
                const Icon = platform?.icon || LinkIcon;
                return (
                  <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                    <div className="flex items-center gap-2">
                      <Icon className="h-4 w-4" />
                      <span className="text-sm">{platform?.name || social.platform}</span>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => removeSocialLink(idx)}>
                      Remove
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
          
          {/* Add new social */}
          {formData.linked_socials.length < 3 && (
            <div className="space-y-3 p-4 rounded-lg border border-dashed">
              <div className="grid grid-cols-2 gap-2">
                {SOCIAL_PLATFORMS.map(platform => (
                  <button
                    key={platform.id}
                    onClick={() => setNewSocial({ ...newSocial, platform: platform.id })}
                    className={`flex items-center gap-2 p-2 rounded-lg text-sm transition-colors ${
                      newSocial.platform === platform.id 
                        ? 'bg-primary text-primary-foreground' 
                        : 'bg-muted/50 hover:bg-muted'
                    }`}
                  >
                    <platform.icon className="h-4 w-4" />
                    {platform.name}
                  </button>
                ))}
              </div>
              
              {newSocial.platform && (
                <div className="flex gap-2">
                  <Input
                    placeholder={SOCIAL_PLATFORMS.find(p => p.id === newSocial.platform)?.placeholder}
                    value={newSocial.url}
                    onChange={(e) => setNewSocial({ ...newSocial, url: e.target.value })}
                    className="flex-1"
                  />
                  <Button onClick={addSocialLink} size="sm">Add</Button>
                </div>
              )}
            </div>
          )}
          
          <Button 
            onClick={handleSaveProfile} 
            disabled={formData.linked_socials.length === 0 || saving}
            className="w-full"
            data-testid="save-socials-btn"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Continue
          </Button>
          
          <Button 
            variant="ghost" 
            onClick={() => setCurrentStep(prev => prev + 1)}
            className="w-full text-muted-foreground"
          >
            Skip for now
          </Button>
        </div>
      )
    },
    {
      key: 'id_verification',
      title: 'Verify your identity',
      description: 'Scan your ID to earn a verified badge and build trust',
      icon: Shield,
      content: (
        <div className="space-y-4">
          <div className="p-4 rounded-xl bg-primary/10 border border-primary/30">
            <div className="flex items-start gap-3">
              <Shield className="h-6 w-6 text-primary mt-1" />
              <div>
                <p className="font-medium">Why verify?</p>
                <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                  <li>• Get a verified badge on your profile</li>
                  <li>• Build trust with clients and bookees</li>
                  <li>• Required for messaging and booking</li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="p-4 rounded-xl bg-muted/50">
            <p className="text-sm text-muted-foreground mb-3">
              You will need to provide a valid government-issued ID (passport, drivers license, or ID card) and take a selfie.
            </p>
          </div>
          
          <Button 
            onClick={handleStartVerification} 
            disabled={saving}
            className="w-full"
            data-testid="start-verification-btn"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Shield className="h-4 w-4 mr-2" />}
            Start ID Verification
          </Button>
          
          <Button 
            variant="ghost" 
            onClick={() => setCurrentStep(prev => prev + 1)}
            className="w-full text-muted-foreground"
          >
            I will do this later
          </Button>
        </div>
      )
    },
    {
      key: 'payment_method',
      title: 'Add payment method',
      description: 'Add a card to complete bookings',
      icon: CreditCard,
      content: (
        <div className="space-y-4">
          <div className="p-4 rounded-xl bg-muted/50">
            <p className="text-sm text-muted-foreground">
              Adding a payment method allows you to book services and receive payments securely through Bookd.
            </p>
          </div>
          
          <Button 
            onClick={handleAddPaymentMethod} 
            disabled={saving}
            className="w-full"
            data-testid="add-payment-btn"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CreditCard className="h-4 w-4 mr-2" />}
            Add Payment Method
          </Button>
        </div>
      )
    }
  ];

  if (loading) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-md">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // If profile is already complete
  if (completionStatus?.profile_completed) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-500" />
              Profile Complete!
            </DialogTitle>
            <DialogDescription>
              Your profile is complete. You can now message and book on Bookd.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="flex items-center justify-center">
              <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center">
                <CheckCircle className="h-10 w-10 text-green-500" />
              </div>
            </div>
          </div>
          <Button onClick={handleComplete} className="w-full">
            Continue
          </Button>
        </DialogContent>
      </Dialog>
    );
  }

  const currentStepData = steps[currentStep];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Syne, sans-serif' }}>
            Complete Your Profile
          </DialogTitle>
          <DialogDescription>
            Complete these steps to start messaging and booking on Bookd
          </DialogDescription>
        </DialogHeader>

        {/* Progress */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Progress</span>
            <span className="font-medium">{completionStatus?.completion_percent || 0}%</span>
          </div>
          <Progress value={completionStatus?.completion_percent || 0} className="h-2" />
        </div>

        {/* Steps indicators */}
        <div className="flex items-center justify-between py-4">
          {steps.map((step, idx) => {
            const isCompleted = completionStatus?.requirements?.find(r => r.key === step.key)?.completed;
            const isCurrent = idx === currentStep;
            const Icon = step.icon;
            
            return (
              <button
                key={step.key}
                onClick={() => setCurrentStep(idx)}
                className={`flex flex-col items-center gap-1 p-2 rounded-lg transition-colors ${
                  isCurrent ? 'bg-primary/10' : ''
                }`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  isCompleted 
                    ? 'bg-green-500 text-white' 
                    : isCurrent 
                      ? 'bg-primary text-primary-foreground' 
                      : 'bg-muted'
                }`}>
                  {isCompleted ? (
                    <CheckCircle className="h-4 w-4" />
                  ) : (
                    <Icon className="h-4 w-4" />
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {/* Current step content */}
        {currentStepData && (
          <div className="space-y-4">
            <div>
              <h3 className="font-medium">{currentStepData.title}</h3>
              <p className="text-sm text-muted-foreground">{currentStepData.description}</p>
            </div>
            {currentStepData.content}
          </div>
        )}

        {/* Skip all */}
        <div className="pt-4 border-t">
          <Button 
            variant="ghost" 
            onClick={onClose}
            className="w-full text-muted-foreground"
          >
            Complete later
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProfileCompletionModal;
